import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, Pencil, Trash2, Eye } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatMaxLevels } from '@/lib/approval-pattern-selection';
import { NotificationSettingsMatrix } from '@/components/NotificationSettingsMatrix';

/**
 * `/settings/request-types` (TD-28 段階③)。
 * 汎用申請ワークフロー・プラットフォームの申請種別マスタ + フィールド定義 + 条件付き表示/必須 +
 * 計算式を管理する。3段階権限 (①request_type_fields ②request_type_semantic ③request_type_routing、
 * いずれも all_stores 限定) に応じて編集可能な範囲が変わる。
 * 設計: docs/design/td28-request-workflow-platform-detail.md §2/§3/§5/§6/§10
 *
 * 式 (条件付き表示/必須・計算式) は JSON テキストで直接編集する (既存の tenure_rules_json 等、
 * 複雑なネスト設定を raw JSON textarea で扱う前例に倣う)。構造検証・意味検証はサーバー側で行い、
 * エラーメッセージをそのまま表示する。
 */

const CATEGORY_LABELS: Record<string, string> = {
  statutory_leave: '法定休暇', discretionary_leave: '法定外休暇', work_adjustment: '勤務調整 (残業等)',
  personal_info_change: '個人情報変更', other: 'その他',
};
const TIMING_MODE_LABELS: Record<string, string> = { standard: '都度申請', pre_approval: '事前許可', post_hoc: '事後記録' };
const HANDLER_KEY_LABELS: Record<string, string> = {
  leave_grant_consumption: '休暇消化', overtime_estimate_link: '残業実績連携',
  personal_info_pending_change: '個人情報の保留反映', record_only: '記録のみ (承認以外の処理なし)',
};
// ⚠️ apps/api/src/lib/request-types/constants.ts の FIELD_TYPES と1対1で対応させること
//    (§8-93/§8-55 の二重定義)。ここに無い型は選択肢に出ないため、API 側だけ追加すると
//    「その型でしか使えない semantic_role」が管理UIから到達不能な dead role になる
//    (§16-3 CRITICAL / §8-99 / §8-122 と同型。platform-reviewer が検出)。
const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'テキスト', textarea: '長文テキスト', number: '数値', date: '日付', date_range: '期間 (開始/終了)',
  multi_date: '日付 (複数選択)', time: '時刻',
  select: '選択肢 (単一)', multi_select: '選択肢 (複数)', boolean: '真偽値', file: 'ファイル',
};
// ⚠️ apps/api/src/lib/request-types/constants.ts の FIELD_CONDITION_EFFECTS と1対1で対応させること
// (§8-93/§8-152 型の二重定義。api/src/lib/__tests__/web-static-dictionaries.test.ts が集合一致を検証する)。
const EFFECT_LABELS: Record<string, string> = {
  show: '表示する',
  hide: '非表示にする',
  require: '必須にする',
  optional: '任意にする',
  error: 'エラーにする (相関チェック)',
};

type RequestType = {
  request_type_id: string; category: string; code: string; label: string;
  platform_catalog_ref: string | null; domain_ref: string | null; timing_mode: string;
  eligibility_condition_set_id: string | null; approval_usage_point_id: string | null;
  // 使い勝手フィードバック④ (D-U19): この申請種別が使う承認パターンを直接指定する。
  approval_workflow_id: string | null;
  // 使い勝手フィードバック⑤a (D-U15/D-U17): 決裁画面のボタン選択肢と相対の巻き戻し段数。
  rejection_behavior: string; return_step_back: number;
  // 使い勝手フィードバック⑦ (D-U38): 代理申請の可否・代理申請者条件。
  proxy_request_mode: string; proxy_requester_condition_set_id: string | null;
  handler_key: string; is_platform_mandatory: boolean; status: string; sort_order: number;
};
type FieldDefinition = {
  field_definition_id: string; request_type_id: string; field_key: string; field_type: string;
  // 使い勝手フィードバック② (D-U25): label はテナントが設定した生の値 (未設定なら null)、
  // resolved_label はサーバーが解決した最終的な表示名 (常に非 null)。
  label: string | null; resolved_label: string;
  field_options: Array<{ value: string; label: string }> | null; semantic_role: string | null;
  visibility_class_id?: string | null; section_label?: string | null; section_order?: number;
  is_computed: boolean; is_required_default: boolean; sort_order: number; status: string;
  // 使い勝手フィードバック⑥ (D-U28)
  validation_json?: Record<string, unknown> | null;
};
type FieldCondition = {
  condition_id: string; target_field_definition_id: string; effect: string;
  expression_ast: Record<string, unknown>; sort_order: number;
  // 使い勝手フィードバック⑥ (D-U28): effect='error' のときのメッセージ。
  error_message?: string | null;
};
type FieldFormula = { formula_id: string; target_field_definition_id: string; expression_ast: Record<string, unknown> };
type SemanticRole = { role_key: string; label: string; applicable_categories: string[]; value_kind: string; is_implemented: boolean };
type UsagePoint = { usage_point_id: string; label: string; workflow_type: string };
type ConditionSetSummary = { set_id: string; label: string | null };
// 使い勝手フィードバック④ (D-U19)
type WorkflowPattern = { workflow_id: string; workflow_type: string; code: string; label: string; max_levels: number; is_active: boolean };
/** 選択中パターンの補足キャプション用。0段階は「最大◯段階」ではなく通知のみと明示する (D-U18)。
 *  短縮版 (`formatMaxLevels`) はドロップダウンの選択肢ラベルに使う (`lib/approval-pattern-selection.ts`)。 */
const formatMaxLevelsLong = (maxLevels: number): string =>
  maxLevels === 0 ? '承認は行わず、共有先へ通知のみ行います' : `最大${maxLevels}段階`;

const errMsg = (e: unknown, fallback: string) => (e instanceof ApiClientError ? e.message : fallback);

/** ⚠️ FieldFormDialog (プレビュー用) と RequestTypeDetailPanel (保存用) の両方が使うため
 *  モジュールスコープに置く (§8-55 対策)。 */
const parseOptionsText = (text: string): Array<{ value: string; label: string }> | null => {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return null;
  return lines.map((line) => {
    const [value, ...rest] = line.split('=');
    return { value: (value ?? '').trim(), label: (rest.join('=') || value || '').trim() };
  });
};

// ---------------------------------------------------------------------------
// 申請種別 (base属性) の追加/編集ダイアログ
// ---------------------------------------------------------------------------
type RequestTypeFormState = {
  category: string; code: string; label: string; timing_mode: string; handler_key: string;
  eligibility_condition_set_id: string | null; approval_usage_point_id: string | null;
  // TD-28 段階⑥: 休暇カテゴリの申請種別が消化対象とする休暇種別 (leave_types)。
  // 未設定だと leave_grant_consumption ハンドラが必ず反映失敗するため、休暇カテゴリでは実質必須。
  domain_ref: string | null;
  // 使い勝手フィードバック④ (D-U19): NULL = 利用箇所の既定 (ノード別binding+既定パターン) に従う。
  approval_workflow_id: string | null;
  // 使い勝手フィードバック⑤a (D-U17): 'reject_only' | 'return_only' | 'both'。
  rejection_behavior: string;
  // 使い勝手フィードバック⑤b (D-U15): 0=申請者まで戻す、1以上=その段階数だけ前へ戻す。
  return_step_back: number;
  // 使い勝手フィードバック⑦ (D-U38): 代理申請の可否・代理申請者条件。
  proxy_request_mode: string;
  proxy_requester_condition_set_id: string | null;
};

// 使い勝手フィードバック⑤a (D-U17)
const REJECTION_BEHAVIOR_LABELS: Record<string, string> = {
  reject_only: '承認する / 却下する', return_only: '承認する / 差し戻す', both: '承認する / 差し戻す / 却下する',
};

// 使い勝手フィードバック⑦ (D-U38)
const PROXY_REQUEST_MODE_LABELS: Record<string, string> = {
  inherit: '通常の権限に従う (店長等は担当店舗の代理申請が可能)',
  self_only: '本人のみ (代理申請を禁止)',
  condition: '条件を満たす人のみ代理申請可',
};

type LeaveTypeOption = { leave_type_id: string; code: string; label: string };
const isLeaveCategory = (category: string): boolean =>
  category === 'statutory_leave' || category === 'discretionary_leave';

function RequestTypeFormDialog({ open, initial, canRouting, onClose, onSubmit, submitting }: {
  open: boolean; initial: RequestTypeFormState; canRouting: boolean; onClose: () => void;
  onSubmit: (v: RequestTypeFormState) => void; submitting: boolean;
}) {
  const [form, setForm] = useState<RequestTypeFormState>(initial);
  const NULL_VALUE = '__none__';
  const { data: conditionSets } = useQuery<{ sets: ConditionSetSummary[] }>({
    queryKey: ['condition-sets', 'request_type_eligibility'],
    queryFn: () => apiFetch('/condition-sets?usage_point_id=request_type_eligibility'),
    enabled: open && canRouting,
  });
  // 使い勝手フィードバック⑦ (D-U38): 代理申請者条件 (mode='condition' のときのみ選択肢が要る)
  const { data: proxyConditionSets } = useQuery<{ sets: ConditionSetSummary[] }>({
    queryKey: ['condition-sets', 'request_type_proxy_requester'],
    queryFn: () => apiFetch('/condition-sets?usage_point_id=request_type_proxy_requester'),
    enabled: open && canRouting && form.proxy_request_mode === 'condition',
  });
  const { data: leaveTypes } = useQuery<LeaveTypeOption[]>({
    queryKey: ['leave-types'],
    queryFn: () => apiFetch<LeaveTypeOption[]>('/leave-types'),
    enabled: open && canRouting && isLeaveCategory(form.category),
  });
  const { data: usagePoints } = useQuery<UsagePoint[]>({
    queryKey: ['approvals-usage-points'],
    queryFn: () => apiFetch('/approvals/usage-points'),
    enabled: open && canRouting,
  });
  // 使い勝手フィードバック④ (D-U19): 選択中の利用箇所と workflow_type が一致するパターンのみ選べる。
  const selectedUsagePointWorkflowType = usagePoints?.find((u) => u.usage_point_id === form.approval_usage_point_id)?.workflow_type ?? null;
  const { data: workflowPatterns } = useQuery<WorkflowPattern[]>({
    queryKey: ['approvals-workflow-patterns', selectedUsagePointWorkflowType],
    queryFn: () => apiFetch(`/approvals/workflow-patterns?workflow_type=${encodeURIComponent(selectedUsagePointWorkflowType!)}`),
    enabled: open && canRouting && !!selectedUsagePointWorkflowType,
  });
  const selectedPattern = workflowPatterns?.find((p) => p.workflow_id === form.approval_workflow_id) ?? null;

  if (!open) return null;
  return (
    <Modal open={open} title={initial.code ? '申請種別を編集' : '申請種別を追加'} onClose={onClose} size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" form="request-type-form" type="submit" loading={submitting}>保存</Button>
        </>
      }
    >
      <form id="request-type-form" className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        // D-U33: パターン変更でゼロ段階承認になった場合、UI上は無効化しているが選択済みの
        // 値が古いまま残りうるため、送信直前に強制する (サーバー側でも同じ組合せは拒否される)。
        onSubmit(selectedPattern?.max_levels === 0 ? { ...form, rejection_behavior: 'reject_only', return_step_back: 0 } : form);
      }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">カテゴリ*</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">タイミング</label>
            <Select value={form.timing_mode} onValueChange={(v) => setForm({ ...form, timing_mode: v })}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TIMING_MODE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">コード* (テナント内で一意)</label>
          <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">名称*</label>
          <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">ハンドラ</label>
          <Select value={form.handler_key} onValueChange={(v) => setForm({ ...form, handler_key: v })}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(HANDLER_KEY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className={`space-y-3 pt-2 border-t border-surface-border ${canRouting ? '' : 'opacity-50'}`}>
          <p className="text-xs text-text-sub">対象者・承認接続 (request_type_routing 権限が必要)</p>
          <div>
            <label className="block text-sm text-text-sub mb-1">対象者条件セット</label>
            <Select
              value={form.eligibility_condition_set_id ?? NULL_VALUE}
              onValueChange={(v) => setForm({ ...form, eligibility_condition_set_id: v === NULL_VALUE ? null : v })}
              disabled={!canRouting}
            >
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="全員 (未指定)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NULL_VALUE}>全員 (未指定)</SelectItem>
                {(conditionSets?.sets ?? []).map((s) => <SelectItem key={s.set_id} value={s.set_id}>{s.label ?? s.set_id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* 使い勝手フィードバック⑦ (D-U38): 代理申請の可否・代理申請者条件。 */}
          <div>
            <label className="block text-sm text-text-sub mb-1">代理申請</label>
            <Select
              value={form.proxy_request_mode}
              onValueChange={(v) => setForm({ ...form, proxy_request_mode: v, proxy_requester_condition_set_id: v === 'condition' ? form.proxy_requester_condition_set_id : null })}
              disabled={!canRouting}
            >
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PROXY_REQUEST_MODE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.proxy_request_mode === 'condition' && (
            <div>
              <label className="block text-sm text-text-sub mb-1">代理申請者条件</label>
              <Select
                value={form.proxy_requester_condition_set_id ?? NULL_VALUE}
                onValueChange={(v) => setForm({ ...form, proxy_requester_condition_set_id: v === NULL_VALUE ? null : v })}
                disabled={!canRouting}
              >
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="未設定 (誰も代理申請できません)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NULL_VALUE}>未設定 (誰も代理申請できません)</SelectItem>
                  {(proxyConditionSets?.sets ?? []).map((s) => <SelectItem key={s.set_id} value={s.set_id}>{s.label ?? s.set_id}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-text-sub mt-1">
                条件セットは<a href="/settings/conditions" className="text-brand-red underline">条件集合設定 (/settings/conditions)</a>の「代理申請者の条件」タブで作成します。
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm text-text-sub mb-1">承認利用箇所</label>
            <Select
              value={form.approval_usage_point_id ?? NULL_VALUE}
              onValueChange={(v) => setForm({
                ...form, approval_usage_point_id: v === NULL_VALUE ? null : v,
                // ⚠️ 利用箇所を変えると workflow_type が変わりうるため、選択済みのパターン指定は解除する
                // (approval_usage_point_id が NULL のときに approval_workflow_id だけ残ると保存時に400になる)
                approval_workflow_id: null,
              })}
              disabled={!canRouting}
            >
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="承認なし" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NULL_VALUE}>承認なし</SelectItem>
                {(usagePoints ?? []).map((u) => <SelectItem key={u.usage_point_id} value={u.usage_point_id}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* 使い勝手フィードバック④ (D-U19): 申請書ごとに承認パターンを直接指定する。 */}
          {form.approval_usage_point_id && (
            <div>
              <label className="block text-sm text-text-sub mb-1">承認パターン</label>
              <Select
                value={form.approval_workflow_id ?? NULL_VALUE}
                onValueChange={(v) => setForm({ ...form, approval_workflow_id: v === NULL_VALUE ? null : v })}
                disabled={!canRouting}
              >
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="利用箇所の既定に従う" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NULL_VALUE}>利用箇所の既定に従う</SelectItem>
                  {(workflowPatterns ?? []).map((p) => (
                    <SelectItem key={p.workflow_id} value={p.workflow_id}>{p.label} ({formatMaxLevels(p.max_levels)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* design-expertレビューMEDIUM指摘反映: 既定(未選択)が最も選ばれる状態のため、
                  そちらにこそ説明を出す (「消化する休暇種別」欄と同じく常時ヒント表示に揃える)。 */}
              <p className="text-xs text-text-sub mt-1">
                {selectedPattern ? formatMaxLevelsLong(selectedPattern.max_levels)
                  : 'ノード別の割当設定、それも無ければ既定の承認パターンに従います。'}
              </p>
            </div>
          )}
          {/* 使い勝手フィードバック⑤a (D-U17/D-U33): 決裁画面のボタン選択肢。
              ⚠️ D-U33: ゼロ段階承認 (承認パターンの段階数=0) のパターンに差し戻しを組み合わせると
              承認申請自体が作られず差し戻しが起きえないため、選択できないようにする。 */}
          <div>
            <label className="block text-sm text-text-sub mb-1">決裁画面の選択肢</label>
            <Select
              value={form.rejection_behavior}
              onValueChange={(v) => setForm({ ...form, rejection_behavior: v })}
              disabled={!canRouting || selectedPattern?.max_levels === 0}
            >
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(REJECTION_BEHAVIOR_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedPattern?.max_levels === 0 && (
              <p className="text-xs text-text-sub mt-1">
                ゼロ段階承認のパターンでは差し戻しを設定できません (D-U33)。
              </p>
            )}
          </div>
          {/* 使い勝手フィードバック⑤b (D-U15): 決裁画面で「差し戻す」を選べる場合のみ戻り先を設定する。
              ⚠️ 選択肢は選択中の承認パターンの max_levels に応じて出す (詳細設計 §6-7)。
              利用箇所の既定に従う場合 (max_levels が確定しない) は保守的に0/1/2を提示する
              (境界を超えた値でも resolveReturnTarget が申請者まで戻すへ安全にフォールバックする、D-U15)。 */}
          {form.rejection_behavior !== 'reject_only' && (
            <div>
              <label className="block text-sm text-text-sub mb-1">戻り先</label>
              <Select
                value={String(form.return_step_back)}
                onValueChange={(v) => setForm({ ...form, return_step_back: Number(v) })}
                disabled={!canRouting}
              >
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">申請者まで戻す</SelectItem>
                  {Array.from(
                    { length: Math.max(0, (selectedPattern?.max_levels ?? 3) - 1) },
                    (_, i) => i + 1,
                  ).map((n) => <SelectItem key={n} value={String(n)}>{n}段階前の承認者まで戻す</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {isLeaveCategory(form.category) && (
          <div>
            <label className="block text-sm text-text-sub mb-1">消化する休暇種別</label>
            <Select
              value={form.domain_ref ?? NULL_VALUE}
              onValueChange={(v) => setForm({ ...form, domain_ref: v === NULL_VALUE ? null : v })}
              disabled={!canRouting}
            >
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="未設定" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NULL_VALUE}>未設定</SelectItem>
                {(leaveTypes ?? []).map((lt) => <SelectItem key={lt.leave_type_id} value={lt.leave_type_id}>{lt.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-text-sub mt-1">
              承認後にどの休暇の残数を消化するかを指定します。未設定のままだと承認は通っても残数に反映されません。
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 式 (条件/計算式) の JSON エディタ
// ---------------------------------------------------------------------------
const AST_HINT = `使える演算子: add/sub/mul/div, eq/neq/gt/gte/lt/lte, and/or/not, date_diff_days, if
{"op":"field_ref","field_definition_id":"RTF-xxxxx"} でフィールド値を参照
{"op":"literal","value":...} で固定値
例: {"op":"gt","left":{"op":"field_ref","field_definition_id":"RTF-xxxx"},"right":{"op":"literal","value":0}}`;

function AstJsonEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <textarea
        value={value} onChange={(e) => onChange(e.target.value)} rows={4} spellCheck={false}
        className="w-full px-3 py-2 rounded border border-surface-border font-mono text-xs focus-ring"
      />
      <details className="mt-1">
        <summary className="text-xs text-text-sub cursor-pointer">式の書き方</summary>
        <pre className="text-xs text-text-sub whitespace-pre-wrap mt-1">{AST_HINT}</pre>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 使い勝手フィードバック⑥ (D-U28): 入力の制限 (層1: 上限・範囲、層2: 書式パターン)
// ---------------------------------------------------------------------------
type ValidationConstraints = Record<string, unknown>;
type ValidationFormat = { format: string; label: string; example: string };

// ⚠️ apps/api/src/lib/request-types/field-validation.ts の APPLICABLE_KEYS と対応させること。
const CONSTRAINT_KEYS_BY_TYPE: Record<string, string[]> = {
  text: ['min_length', 'max_length', 'format'],
  textarea: ['min_length', 'max_length'],
  number: ['min', 'max', 'decimal_places'],
  date: ['min_date', 'max_date'],
  date_range: ['min_date', 'max_date'],
  multi_date: ['min_date', 'max_date', 'min_items', 'max_items'],
  multi_select: ['min_items', 'max_items'],
  // 使い勝手フィードバック増分⑧ (D-U31)
  file: ['max_file_count'],
};

// ⚠️ design-expert Critical指摘: type="number" はネイティブスピナーを抑制すること
// (NotificationsSettingsPage.tsx/Agreement36SettingsPage.tsx/VisitorCountsPage.tsx/CompensationSettingsPage.tsx
// で確立済みのパターン)。タップ領域も44px (モバイル) を確保する。
const numberInputCls = 'w-full min-h-[44px] sm:min-h-[38px] px-2 rounded border border-surface-border text-sm '
  + 'focus-ring appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

function NumberConstraintField({ label, value, onChange, min, max }: {
  label: string; value: number | undefined; onChange: (v: number | undefined) => void; min?: number; max?: number;
}) {
  return (
    <label className="text-xs text-text-sub space-y-1 block">
      <span>{label}</span>
      <input type="number" min={min} max={max} className={numberInputCls}
        value={typeof value === 'number' ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
    </label>
  );
}

function DateConstraintField({ label, value, onChange }: {
  label: string; value: string | undefined; onChange: (v: string | undefined) => void;
}) {
  return (
    <label className="text-xs text-text-sub space-y-1 block">
      <span>{label}</span>
      <input type="text" placeholder="today / today+7d / YYYY-MM-DD" className={numberInputCls}
        value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)} />
    </label>
  );
}

/** 項目編集モーダルの「入力の制限」セクション (D-U28 §7-5)。
 *  ⚠️ 対象外の field_type では何も描画しない (未対応型に制限を持ち込ませない)。 */
function ValidationConstraintsEditor({ fieldType, fieldOptions, value, onChange }: {
  fieldType: string;
  fieldOptions: Array<{ value: string; label: string }> | null;
  value: ValidationConstraints | null;
  onChange: (v: ValidationConstraints | null) => void;
}) {
  const keys = CONSTRAINT_KEYS_BY_TYPE[fieldType] ?? [];
  const { data: formats } = useQuery<ValidationFormat[]>({
    queryKey: ['validation-formats'],
    queryFn: () => apiFetch('/request-types/validation-formats'),
    enabled: keys.includes('format'),
  });
  // 使い勝手フィードバック⑥ (D-U28、QAレビュー MEDIUM指摘): field_type ごとに異なる形の値を
  // 要求する (date_range={start,end}、multi_date/multi_select=配列) ため、単一のテキスト欄では
  // 7型中3型で「必ず型エラーになり試せない」状態だった。型に応じて入力欄を出し分ける。
  const [sampleValue, setSampleValue] = useState('');
  const [sampleValue2, setSampleValue2] = useState('');
  const [previewResult, setPreviewResult] = useState<{ valid: boolean; error: string | null } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const NULL_VALUE = '__none__';

  if (keys.length === 0) return null;

  const v: ValidationConstraints = value ?? {};
  const setKey = (key: string, val: unknown) => {
    const next: ValidationConstraints = { ...v };
    if (val === undefined || val === '') delete next[key];
    else next[key] = val;
    onChange(Object.keys(next).length === 0 ? null : next);
  };

  const resetPreview = () => setPreviewResult(null);

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewResult(null);
    let sample: unknown;
    if (fieldType === 'number') {
      if (sampleValue === '') { sample = null; } else {
        const n = Number(sampleValue);
        // ⚠️ QAレビュー LOW指摘: "abc" 等の非数値は JSON.stringify で null に落ち
        // 「空欄」と誤認されて valid:true になっていた。ここで明示的に弾く。
        if (Number.isNaN(n)) { setPreviewResult({ valid: false, error: '数値を入力してください' }); setPreviewing(false); return; }
        sample = n;
      }
    } else if (fieldType === 'date_range') {
      sample = (sampleValue === '' && sampleValue2 === '') ? null : { start: sampleValue, end: sampleValue2 };
    } else if (fieldType === 'multi_date' || fieldType === 'multi_select') {
      const items = sampleValue.split(',').map((s) => s.trim()).filter(Boolean);
      sample = items.length === 0 ? null : items;
    } else {
      sample = sampleValue;
    }
    try {
      const res = await apiFetch<{ valid: boolean; error: string | null }>('/request-types/fields/preview-validation', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_type: fieldType, field_options: fieldOptions, validation_json: value, sample_value: sample }),
      });
      setPreviewResult(res);
    } catch (e) {
      setPreviewResult({ valid: false, error: errMsg(e, '確認に失敗しました') });
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="space-y-2 border border-surface-border rounded-md p-3">
      <p className="text-sm text-text-sub font-medium">入力の制限 (任意)</p>
      <div className="grid grid-cols-2 gap-2">
        {keys.includes('min_length') && <NumberConstraintField label="最小文字数" min={0} value={typeof v.min_length === 'number' ? v.min_length : undefined} onChange={(n) => setKey('min_length', n)} />}
        {keys.includes('max_length') && <NumberConstraintField label="最大文字数" min={1} value={typeof v.max_length === 'number' ? v.max_length : undefined} onChange={(n) => setKey('max_length', n)} />}
        {keys.includes('min') && <NumberConstraintField label="最小値" value={typeof v.min === 'number' ? v.min : undefined} onChange={(n) => setKey('min', n)} />}
        {keys.includes('max') && <NumberConstraintField label="最大値" value={typeof v.max === 'number' ? v.max : undefined} onChange={(n) => setKey('max', n)} />}
        {keys.includes('decimal_places') && <NumberConstraintField label="小数点以下の桁数" min={0} value={typeof v.decimal_places === 'number' ? v.decimal_places : undefined} onChange={(n) => setKey('decimal_places', n)} />}
        {keys.includes('min_date') && <DateConstraintField label="最も早い日付" value={typeof v.min_date === 'string' ? v.min_date : undefined} onChange={(s) => setKey('min_date', s)} />}
        {keys.includes('max_date') && <DateConstraintField label="最も遅い日付" value={typeof v.max_date === 'string' ? v.max_date : undefined} onChange={(s) => setKey('max_date', s)} />}
        {keys.includes('min_items') && <NumberConstraintField label="最小件数" min={0} value={typeof v.min_items === 'number' ? v.min_items : undefined} onChange={(n) => setKey('min_items', n)} />}
        {keys.includes('max_items') && <NumberConstraintField label="最大件数" min={1} value={typeof v.max_items === 'number' ? v.max_items : undefined} onChange={(n) => setKey('max_items', n)} />}
        {keys.includes('max_file_count') && <NumberConstraintField label="添付できる件数の上限 (5件まで)" min={1} max={5} value={typeof v.max_file_count === 'number' ? v.max_file_count : undefined} onChange={(n) => setKey('max_file_count', n)} />}
      </div>
      {keys.includes('format') && (
        <label className="text-xs text-text-sub space-y-1 block">
          <span>書式パターン</span>
          <Select value={typeof v.format === 'string' ? v.format : NULL_VALUE}
            onValueChange={(val) => setKey('format', val === NULL_VALUE ? undefined : val)}>
            <SelectTrigger className="w-full min-h-[38px]"><SelectValue placeholder="指定しない" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NULL_VALUE}>指定しない</SelectItem>
              {(formats ?? []).map((fmt) => <SelectItem key={fmt.format} value={fmt.format}>{fmt.label} (例: {fmt.example})</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
      )}
      {/* 使い勝手フィードバック増分⑧: file 型は値ベースの検証を持たない (添付は別テーブルで
          管理するため)。サンプル値で試す欄は意味を持たないため出さない。 */}
      {fieldType !== 'file' && <div className="flex flex-wrap items-center gap-2 pt-1">
        {fieldType === 'date_range' ? (
          <>
            <input type="date" value={sampleValue} onChange={(e) => { setSampleValue(e.target.value); resetPreview(); }}
              aria-label="開始日のサンプル値" className={numberInputCls} />
            <span aria-hidden="true">〜</span>
            <input type="date" value={sampleValue2} onChange={(e) => { setSampleValue2(e.target.value); resetPreview(); }}
              aria-label="終了日のサンプル値" className={numberInputCls} />
          </>
        ) : fieldType === 'multi_date' ? (
          <input type="text" placeholder="サンプル値で試す (日付をカンマ区切り、例: 2026-01-01,2026-01-02)" value={sampleValue}
            onChange={(e) => { setSampleValue(e.target.value); resetPreview(); }}
            className="flex-1 min-h-[38px] px-2 rounded border border-surface-border text-sm focus-ring" />
        ) : fieldType === 'multi_select' ? (
          <input type="text" placeholder="サンプル値で試す (選択肢の値をカンマ区切り)" value={sampleValue}
            onChange={(e) => { setSampleValue(e.target.value); resetPreview(); }}
            className="flex-1 min-h-[38px] px-2 rounded border border-surface-border text-sm focus-ring" />
        ) : fieldType === 'date' ? (
          <input type="date" value={sampleValue} onChange={(e) => { setSampleValue(e.target.value); resetPreview(); }}
            aria-label="サンプル値" className={numberInputCls} />
        ) : (
          <input type={fieldType === 'number' ? 'text' : 'text'} inputMode={fieldType === 'number' ? 'decimal' : undefined}
            placeholder="サンプル値で試す" value={sampleValue} onChange={(e) => { setSampleValue(e.target.value); resetPreview(); }}
            className="flex-1 min-h-[38px] px-2 rounded border border-surface-border text-sm focus-ring" />
        )}
        <Button type="button" size="sm" variant="secondary" loading={previewing} onClick={() => void runPreview()}>試す</Button>
      </div>}
      {previewResult && (
        <p role="status" className={`text-xs ${previewResult.valid ? 'text-emerald-700' : 'text-alert-text'}`}>
          {previewResult.valid ? '✓ この値は制限を満たします' : previewResult.error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// フィールド定義パネル
// ---------------------------------------------------------------------------
function FieldFormDialog({ open, initial, category, semanticRoles, canSemantic, visibilityClasses, canVisibility, onClose, onSubmit, submitting }: {
  open: boolean;
  initial: { field_key: string; field_type: string; label: string | null; field_options: Array<{ value: string; label: string }> | null; semantic_role: string | null; is_computed: boolean; is_required_default: boolean; visibility_class_id: string | null; section_label: string | null; validation_json: ValidationConstraints | null };
  visibilityClasses: Array<{ class_id: string; label: string }>; canVisibility: boolean;
  category: string; semanticRoles: SemanticRole[]; canSemantic: boolean; onClose: () => void;
  onSubmit: (v: typeof initial & { field_options_text: string }) => void; submitting: boolean;
}) {
  const [form, setForm] = useState({ ...initial, field_options_text: initial.field_options ? initial.field_options.map((o) => `${o.value}=${o.label}`).join('\n') : '' });
  const NULL_VALUE = '__none__';
  const applicableRoles = semanticRoles.filter((r) => r.applicable_categories.includes(category));
  if (!open) return null;
  // 使い勝手フィードバック② (D-U25): 未入力時に実際に表示される既定ラベルをプレースホルダに出す
  // (サーバーの解決順 label → semantic_role既定 → field_key と同じ優先順)。
  const defaultLabelPreview = (form.semantic_role && semanticRoles.find((r) => r.role_key === form.semantic_role)?.label)
    || form.field_key || '(未設定)';
  return (
    <Modal open={open} title={initial.field_key ? 'フィールドを編集' : 'フィールドを追加'} onClose={onClose} size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" form="field-form" type="submit" loading={submitting}>保存</Button>
        </>
      }
    >
      <form id="field-form" className="space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}>
        <div>
          <label className="block text-sm text-text-sub mb-1">キー* (半角英数字推奨)</label>
          <input required value={form.field_key} onChange={(e) => setForm({ ...form, field_key: e.target.value })}
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
        </div>
        <div>
          <label htmlFor="rtf-label" className="block text-sm text-text-sub mb-1">表示名 (任意、申請フォームに出る名前)</label>
          <input id="rtf-label" value={form.label ?? ''} maxLength={200}
            onChange={(e) => setForm({ ...form, label: e.target.value === '' ? null : e.target.value })}
            placeholder={defaultLabelPreview}
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
          <p className="text-xs text-text-sub mt-1">未入力なら「{defaultLabelPreview}」と表示されます。</p>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">型</label>
          <Select value={form.field_type} onValueChange={(v) => {
            // 使い勝手フィードバック⑥ (D-U28): 型を変えると層1の対象キーが変わりうる。
            // 新しい型に無いキーを残すと保存時に「型と噛み合わない設定」として拒否されるため、
            // ここで先に落としておく (§7-2)。
            const allowed = new Set(CONSTRAINT_KEYS_BY_TYPE[v] ?? []);
            const pruned = form.validation_json
              ? Object.fromEntries(Object.entries(form.validation_json).filter(([k]) => allowed.has(k)))
              : null;
            setForm({ ...form, field_type: v, validation_json: pruned && Object.keys(pruned).length > 0 ? pruned : null });
          }}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(FIELD_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(form.field_type === 'select' || form.field_type === 'multi_select') && (
          <div>
            <label className="block text-sm text-text-sub mb-1">選択肢 (1行1つ、「値=表示名」形式)</label>
            <textarea value={form.field_options_text} onChange={(e) => setForm({ ...form, field_options_text: e.target.value })}
              rows={3} placeholder={'full_day=全日\nhalf_day_am=午前半休'}
              className="w-full px-3 py-2 rounded border border-surface-border font-mono text-xs focus-ring" />
          </div>
        )}
        {/* 使い勝手フィードバック⑥ (D-U28 §7-5): 「入力の制限」セクション。 */}
        <ValidationConstraintsEditor fieldType={form.field_type} fieldOptions={parseOptionsText(form.field_options_text)}
          value={form.validation_json} onChange={(v) => setForm({ ...form, validation_json: v })} />
        <label className="flex items-center gap-2 text-sm min-h-[44px]">
          <input type="checkbox" className="w-5 h-5 p-[12px] box-content" checked={form.is_required_default}
            onChange={(e) => setForm({ ...form, is_required_default: e.target.checked })} />
          常に必須にする
        </label>
        {/* ⚠️ design-expert H-3 (D-U28): editable=false かつ is_required_default=1 の項目は、
            申請者が永久に提出できなくなる (機密クラスが self 文脈を編集不可にしていた場合)。
            設定時点で気づけるよう警告のみ出す (実行時の除外は増分⑦ 以降の課題として別途扱う)。 */}
        {form.is_required_default && form.visibility_class_id && (
          <p role="alert" className="text-xs text-alert-text">
            機密クラスが設定されています。このクラスが申請者本人からの編集を不可にしていると、
            必須項目のため申請者が永久に提出できなくなります。機密クラスの設定をご確認ください。
          </p>
        )}
        <label className="flex items-center gap-2 text-sm min-h-[44px]">
          <input type="checkbox" className="w-5 h-5 p-[12px] box-content" checked={form.is_computed}
            onChange={(e) => setForm({ ...form, is_computed: e.target.checked })} />
          計算式から値を算出する (読み取り専用)
        </label>
        {/* TD-28 D-24: 機密クラスの割当。⚠️ 「誰がこの項目を見られるか」を変える操作のため
            request_field_visibility:manage を要求する (フィールド編集 tier1 では変更させない) */}
        <div className={canVisibility ? '' : 'opacity-50'}>
          <label className="block text-sm text-text-sub mb-1">機密クラス (表示制御。未設定なら誰にでも表示)</label>
          <Select value={form.visibility_class_id ?? NULL_VALUE}
            onValueChange={(v) => setForm({ ...form, visibility_class_id: v === NULL_VALUE ? null : v })}
            disabled={!canVisibility}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="なし" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NULL_VALUE}>なし (制限しない)</SelectItem>
              {visibilityClasses.map((cl) => <SelectItem key={cl.class_id} value={cl.class_id}>{cl.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* TD-28 D-26: レイアウトは表示順とセクション分けまで */}
        <div>
          <label htmlFor="rtf-section" className="block text-sm text-text-sub mb-1">セクション名 (任意)</label>
          <input id="rtf-section" className="w-full min-h-[44px] rounded-md border border-surface-border px-3 py-2 text-sm"
            value={form.section_label ?? ''}
            onChange={(e) => setForm({ ...form, section_label: e.target.value || null })}
            placeholder="例: 勤務内容" />
        </div>
        <div className={canSemantic ? '' : 'opacity-50'}>
          <label className="block text-sm text-text-sub mb-1">役割 (request_type_semantic 権限が必要)</label>
          <Select value={form.semantic_role ?? NULL_VALUE} onValueChange={(v) => setForm({ ...form, semantic_role: v === NULL_VALUE ? null : v })} disabled={!canSemantic}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="なし" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NULL_VALUE}>なし</SelectItem>
              {applicableRoles.map((r) => <SelectItem key={r.role_key} value={r.role_key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </form>
    </Modal>
  );
}

// 使い勝手フィードバック⑥ (D-U28 §7-5): よく使う相関をテンプレートとして提示する。
// ⚠️ D-U28 は「式を生 JSON で手書きする現状の UI では設定者が使えない」と明記しており、最も
// よく使う「2項目の大小比較」だけでもテンプレートにし、外れるものは既存の JSON エディタへ落とす。
const COMPARISON_OPS: Record<string, string> = { gt: 'より大きい (>)', gte: '以上 (>=)', lt: 'より小さい (<)', lte: '以下 (<=)', eq: '等しい (=)', neq: '等しくない (≠)' };

type ComparisonTemplateState = { targetOp: string; compareFieldId: string };

const buildComparisonAst = (targetFieldId: string, op: string, compareFieldId: string): Record<string, unknown> => ({
  op,
  left: { op: 'field_ref', field_definition_id: targetFieldId },
  right: { op: 'field_ref', field_definition_id: compareFieldId },
});

/** 生成した AST が「よく使う比較」テンプレートの形と一致するか (JSON 手編集後の再表示用)。
 *  一致しなければテンプレートの初期状態には戻さず、詳細モードへ倒す。 */
const parseComparisonTemplate = (targetFieldId: string, astText: string): ComparisonTemplateState | null => {
  try {
    const ast = JSON.parse(astText) as Record<string, unknown>;
    if (!Object.keys(COMPARISON_OPS).includes(ast.op as string)) return null;
    const left = ast.left as Record<string, unknown> | undefined;
    const right = ast.right as Record<string, unknown> | undefined;
    if (left?.op !== 'field_ref' || left.field_definition_id !== targetFieldId) return null;
    if (right?.op !== 'field_ref' || typeof right.field_definition_id !== 'string') return null;
    return { targetOp: ast.op as string, compareFieldId: right.field_definition_id };
  } catch {
    return null;
  }
};

function ConditionFormDialog({ open, fields, initial, onClose, onSubmit, submitting }: {
  open: boolean; fields: FieldDefinition[];
  initial: { target_field_definition_id: string; effect: string; expression_ast_text: string; error_message: string | null };
  onClose: () => void;
  onSubmit: (v: { target_field_definition_id: string; effect: string; expression_ast_text: string; error_message: string | null }) => void;
  submitting: boolean;
}) {
  const [form, setForm] = useState(initial);
  const [mode, setMode] = useState<'template' | 'advanced'>(
    () => (parseComparisonTemplate(initial.target_field_definition_id, initial.expression_ast_text) ? 'template' : 'advanced'),
  );
  const template = mode === 'template' ? parseComparisonTemplate(form.target_field_definition_id, form.expression_ast_text) : null;
  const otherFields = fields.filter((f) => f.field_definition_id !== form.target_field_definition_id);

  if (!open) return null;

  const applyTemplate = (next: ComparisonTemplateState) => {
    setForm({ ...form, expression_ast_text: JSON.stringify(buildComparisonAst(form.target_field_definition_id, next.targetOp, next.compareFieldId)) });
  };

  return (
    <Modal open={open} title="条件付き表示/必須" onClose={onClose} size="sm"
      footer={<><Button variant="secondary" onClick={onClose}>キャンセル</Button><Button variant="primary" form="condition-form" type="submit" loading={submitting}>保存</Button></>}
    >
      <form id="condition-form" className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ ...form, error_message: form.effect === 'error' ? (form.error_message || null) : null });
      }}>
        <div>
          <label className="block text-sm text-text-sub mb-1">対象フィールド</label>
          <Select value={form.target_field_definition_id} onValueChange={(v) => setForm({ ...form, target_field_definition_id: v })}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {fields.map((f) => <SelectItem key={f.field_definition_id} value={f.field_definition_id}>{f.field_key}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">効果</label>
          <Select value={form.effect} onValueChange={(v) => setForm({ ...form, effect: v })}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(EFFECT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* 使い勝手フィードバック⑥ (D-U28): effect='error' のときのメッセージ (必須)。 */}
        {form.effect === 'error' && (
          <div>
            <label htmlFor="rtc-error-message" className="block text-sm text-text-sub mb-1">エラーメッセージ*</label>
            <input id="rtc-error-message" required value={form.error_message ?? ''} maxLength={500}
              onChange={(e) => setForm({ ...form, error_message: e.target.value })}
              placeholder="例: 終了日は開始日以降にしてください"
              className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
          </div>
        )}
        <div className="flex items-center gap-3 text-xs text-text-sub">
          <span>条件式の作り方:</span>
          <label className="inline-flex items-center gap-1">
            <input type="radio" name="rtc-mode" checked={mode === 'template'} onChange={() => setMode('template')} /> よく使う比較
          </label>
          <label className="inline-flex items-center gap-1">
            <input type="radio" name="rtc-mode" checked={mode === 'advanced'} onChange={() => setMode('advanced')} /> 詳細 (JSON)
          </label>
        </div>
        {mode === 'template' ? (
          <div className="space-y-2 border border-surface-border rounded-md p-3">
            <p className="text-xs text-text-sub">対象フィールドが、次の条件を満たすとき効果を適用します。</p>
            <div className="grid grid-cols-2 gap-2">
              <Select value={template?.targetOp ?? 'gte'}
                onValueChange={(op) => applyTemplate({ targetOp: op, compareFieldId: template?.compareFieldId ?? otherFields[0]?.field_definition_id ?? '' })}>
                <SelectTrigger className="w-full min-h-[38px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(COMPARISON_OPS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={template?.compareFieldId ?? ''}
                onValueChange={(v) => applyTemplate({ targetOp: template?.targetOp ?? 'gte', compareFieldId: v })}>
                <SelectTrigger className="w-full min-h-[38px]"><SelectValue placeholder="比較するフィールド" /></SelectTrigger>
                <SelectContent>
                  {otherFields.map((f) => <SelectItem key={f.field_definition_id} value={f.field_definition_id}>{f.field_key}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!template && (
              <p className="text-xs text-alert-text">
                現在の式はこのテンプレートの形と一致しません。上のプルダウンを選び直すと式が上書きされます
                (元の式を残す場合は「詳細 (JSON)」を選んでください)。
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-sm text-text-sub mb-1">条件式 (真のとき効果を適用)</label>
            <AstJsonEditor value={form.expression_ast_text} onChange={(v) => setForm({ ...form, expression_ast_text: v })} />
          </div>
        )}
      </form>
    </Modal>
  );
}

function FormulaFormDialog({ open, fields, initial, onClose, onSubmit, submitting }: {
  open: boolean; fields: FieldDefinition[];
  initial: { target_field_definition_id: string; expression_ast_text: string };
  onClose: () => void; onSubmit: (v: { target_field_definition_id: string; expression_ast_text: string }) => void; submitting: boolean;
}) {
  const [form, setForm] = useState(initial);
  const computedFields = fields.filter((f) => f.is_computed);
  if (!open) return null;
  return (
    <Modal open={open} title="計算式" onClose={onClose} size="sm"
      footer={<><Button variant="secondary" onClick={onClose}>キャンセル</Button><Button variant="primary" form="formula-form" type="submit" loading={submitting}>保存</Button></>}
    >
      <form id="formula-form" className="space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}>
        <div>
          <label className="block text-sm text-text-sub mb-1">算出先フィールド (「計算式から値を算出する」がONのもののみ)</label>
          {computedFields.length === 0 ? (
            <p className="text-xs text-text-sub">計算式を設定できるフィールドがありません。先にフィールドの「計算式から値を算出する」をONにしてください。</p>
          ) : (
            <Select value={form.target_field_definition_id} onValueChange={(v) => setForm({ ...form, target_field_definition_id: v })}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {computedFields.map((f) => <SelectItem key={f.field_definition_id} value={f.field_definition_id}>{f.field_key}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">計算式</label>
          <AstJsonEditor value={form.expression_ast_text} onChange={(v) => setForm({ ...form, expression_ast_text: v })} />
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 使い勝手フィードバック⑦ (D-U36): 提示範囲の管理者向けプレビュー
// ---------------------------------------------------------------------------
type PreviewEmployee = { employee_id: string; employee_number: string; family_name: string; given_name: string };
type AudiencePreviewResult = { visible: boolean; blocked_by: 'status' | 'feature_flag' | 'permission' | 'eligibility' | null; detail: string };

const AUDIENCE_BLOCKED_BY_LABELS: Record<string, string> = {
  status: '申請種別が無効です',
  feature_flag: 'このテナントでこのカテゴリの申請は有効になっていません',
  permission: 'この社員には申請を作成する権限がありません',
  eligibility: 'この社員は利用資格の条件を満たしていません',
};

function AudiencePreview({ requestTypeId, canRouting }: { requestTypeId: string; canRouting: boolean }) {
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const { data: employees = [] } = useQuery<PreviewEmployee[]>({
    queryKey: ['employees'], queryFn: () => apiFetch<PreviewEmployee[]>('/employees'), enabled: canRouting,
  });
  const previewMut = useMutation({
    mutationFn: () => apiFetch<AudiencePreviewResult>(
      `/request-types/${requestTypeId}/preview-audience?employee_id=${encodeURIComponent(employeeId!)}`,
    ),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'プレビューに失敗しました'),
  });

  if (!canRouting) return null;
  const NULL_VALUE = '__none__';
  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Eye size={14} className="text-text-sub shrink-0" aria-hidden />
        <h4 className="text-xs font-semibold text-text-body">提示範囲プレビュー</h4>
      </div>
      <p className="text-xs text-text-sub">社員を選ぶと、この申請書がその人に表示されるかを確認できます。</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 w-full sm:w-64">
          <Select value={employeeId ?? NULL_VALUE} onValueChange={(v) => { setEmployeeId(v === NULL_VALUE ? null : v); previewMut.reset(); }}>
            <SelectTrigger className="w-full min-h-[44px]" aria-label="プレビュー対象の社員"><SelectValue placeholder="社員を選ぶ" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.employee_id} value={e.employee_id}>{e.family_name} {e.given_name} ({e.employee_number})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="secondary" disabled={!employeeId || previewMut.isPending} loading={previewMut.isPending} onClick={() => previewMut.mutate()}>
          確認する
        </Button>
      </div>
      {previewMut.data && (
        <p className="text-xs" aria-live="polite">
          {previewMut.data.visible ? (
            <span className="text-success-text font-semibold">表示されます</span>
          ) : (
            <span className="text-alert-text font-semibold">
              表示されません{previewMut.data.blocked_by && ` (${AUDIENCE_BLOCKED_BY_LABELS[previewMut.data.blocked_by] ?? previewMut.data.blocked_by})`}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function RequestTypeDetailPanel({ requestType, semanticRoles, canFields, canSemantic, visibilityClasses, canVisibility, canRouting }: {
  requestType: RequestType; semanticRoles: SemanticRole[]; canFields: boolean; canSemantic: boolean;
  visibilityClasses: Array<{ class_id: string; label: string }>; canVisibility: boolean; canRouting: boolean;
}) {
  const qc = useQueryClient();
  const rtId = requestType.request_type_id;
  const { data: fields = [], isLoading: fieldsLoading } = useQuery<FieldDefinition[]>({
    queryKey: ['request-type-fields', rtId], queryFn: () => apiFetch(`/request-types/${rtId}/fields`),
  });
  const { data: conditions = [] } = useQuery<FieldCondition[]>({
    queryKey: ['request-type-conditions', rtId], queryFn: () => apiFetch(`/request-types/${rtId}/conditions`),
  });
  const { data: formulas = [] } = useQuery<FieldFormula[]>({
    queryKey: ['request-type-formulas', rtId], queryFn: () => apiFetch(`/request-types/${rtId}/formulas`),
  });
  const fieldsById = useMemo(() => new Map(fields.map((f) => [f.field_definition_id, f])), [fields]);

  const [editingField, setEditingField] = useState<FieldDefinition | 'new' | null>(null);
  const [deletingField, setDeletingField] = useState<FieldDefinition | null>(null);
  const [editingCondition, setEditingCondition] = useState<FieldCondition | 'new' | null>(null);
  const [deletingCondition, setDeletingCondition] = useState<FieldCondition | null>(null);
  const [editingFormula, setEditingFormula] = useState<FieldFormula | 'new' | null>(null);
  const [deletingFormula, setDeletingFormula] = useState<FieldFormula | null>(null);

  const invalidateFields = () => qc.invalidateQueries({ queryKey: ['request-type-fields', rtId] });
  const invalidateConditions = () => qc.invalidateQueries({ queryKey: ['request-type-conditions', rtId] });
  const invalidateFormulas = () => qc.invalidateQueries({ queryKey: ['request-type-formulas', rtId] });

  const saveField = useMutation({
    mutationFn: (v: { id?: string; body: unknown }) => apiFetch(v.id ? `/request-types/${rtId}/fields/${v.id}` : `/request-types/${rtId}/fields`, {
      method: v.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v.body),
    }),
    onSuccess: () => { invalidateFields(); setEditingField(null); toast.success('フィールドを保存しました'); },
    onError: (e) => toast.error(errMsg(e, 'フィールドの保存に失敗しました')),
  });
  const deleteField = useMutation({
    mutationFn: (id: string) => apiFetch(`/request-types/${rtId}/fields/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidateFields(); setDeletingField(null); toast.success('フィールドを無効化しました'); },
    onError: (e) => { toast.error(errMsg(e, '無効化に失敗しました')); setDeletingField(null); },
  });
  const saveCondition = useMutation({
    mutationFn: (v: { id?: string; body: unknown }) => apiFetch(v.id ? `/request-types/${rtId}/conditions/${v.id}` : `/request-types/${rtId}/conditions`, {
      method: v.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v.body),
    }),
    onSuccess: () => { invalidateConditions(); setEditingCondition(null); toast.success('条件を保存しました'); },
    onError: (e) => toast.error(errMsg(e, '条件の保存に失敗しました')),
  });
  const deleteCondition = useMutation({
    mutationFn: (id: string) => apiFetch(`/request-types/${rtId}/conditions/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidateConditions(); setDeletingCondition(null); toast.success('条件を削除しました'); },
    onError: (e) => { toast.error(errMsg(e, '削除に失敗しました')); setDeletingCondition(null); },
  });
  const saveFormula = useMutation({
    mutationFn: (v: { id?: string; body: unknown }) => apiFetch(v.id ? `/request-types/${rtId}/formulas/${v.id}` : `/request-types/${rtId}/formulas`, {
      method: v.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v.body),
    }),
    onSuccess: () => { invalidateFormulas(); setEditingFormula(null); toast.success('計算式を保存しました'); },
    onError: (e) => toast.error(errMsg(e, '計算式の保存に失敗しました')),
  });
  const deleteFormula = useMutation({
    mutationFn: (id: string) => apiFetch(`/request-types/${rtId}/formulas/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidateFormulas(); setDeletingFormula(null); toast.success('計算式を削除しました'); },
    onError: (e) => { toast.error(errMsg(e, '削除に失敗しました')); setDeletingFormula(null); },
  });

  const parseAstText = (text: string): unknown => {
    try {
      return JSON.parse(text);
    } catch {
      toast.error('式の JSON が不正です');
      throw new Error('invalid json');
    }
  };

  // 使い勝手フィードバック⑤a (D-U20、Q-93/D-70 必須要件② と同型のゼロコスト事故防止):
  // 差し戻しを有効にしているのに差し戻し理由の項目が未設定だと、決裁者が入力した理由が
  // どこにも表示されない (D-U20⑤の転記先が無い)。
  const hasReturnReasonField = fields.some((f) => f.semantic_role === 'return_reason');
  const showReturnReasonWarning = requestType.rejection_behavior !== 'reject_only' && !fieldsLoading && !hasReturnReasonField;

  return (
    <div className="border-t border-surface-border divide-y divide-surface-border">
      {showReturnReasonWarning && (
        <div className="p-3 sm:p-4">
          <div role="alert" className="rounded-md bg-alert-bg text-alert-text text-sm px-3 py-2">
            差し戻しを有効にしていますが、差し戻し理由の項目 (役割「差し戻し理由」) が未設定です。
          </div>
        </div>
      )}
      <div className="p-3 sm:p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-text-body">フィールド</h3>
          {canFields && (
            <Button size="sm" variant="secondary" leftIcon={<Plus size={14} />} onClick={() => setEditingField('new')}>フィールド追加</Button>
          )}
        </div>
        {fieldsLoading ? <div className="text-sm text-text-sub">読み込み中…</div> : fields.length === 0 ? (
          <EmptyState message="フィールドが登録されていません" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="text-text-sub"><tr>
                <th className="text-left px-2 py-1">表示名</th><th className="text-left px-2 py-1">キー</th>
                <th className="text-left px-2 py-1">型</th>
                <th className="text-left px-2 py-1">役割</th><th className="text-center px-2 py-1">必須</th>
                <th className="text-center px-2 py-1">計算</th><th className="w-20 px-2 py-1"></th>
              </tr></thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={f.field_definition_id} className="border-t border-surface-border">
                    <td className="px-2 py-2 text-text-body">{f.resolved_label}</td>
                    <td className="px-2 py-2 font-mono text-xs text-text-sub">{f.field_key}</td>
                    <td className="px-2 py-2 text-xs text-text-sub">{FIELD_TYPE_LABELS[f.field_type] ?? f.field_type}</td>
                    <td className="px-2 py-2 text-xs text-text-sub">{semanticRoles.find((r) => r.role_key === f.semantic_role)?.label ?? '—'}</td>
                    <td className="px-2 py-2 text-center">{f.is_required_default ? '○' : ''}</td>
                    <td className="px-2 py-2 text-center">{f.is_computed ? '○' : ''}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {canFields && (
                        <>
                          <button type="button" aria-label="編集" onClick={() => setEditingField(f)} className="p-1 text-brand-red hover:text-brand-red-hover"><Pencil size={14} /></button>
                          <button type="button" aria-label="無効化" onClick={() => setDeletingField(f)} className="ml-1 p-1 text-text-sub hover:text-warning-text"><Trash2 size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="p-3 sm:p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-text-body">条件付き表示/必須</h3>
          {canSemantic && fields.length > 0 && (
            <Button size="sm" variant="secondary" leftIcon={<Plus size={14} />} onClick={() => setEditingCondition('new')}>条件追加</Button>
          )}
        </div>
        {conditions.length === 0 ? <p className="text-xs text-text-sub">登録されていません</p> : (
          <ul className="space-y-1">
            {conditions.map((c) => (
              <li key={c.condition_id} className="flex items-start justify-between gap-2 text-xs border border-surface-border rounded p-2">
                <div>
                  <span className="font-mono">{fieldsById.get(c.target_field_definition_id)?.field_key ?? c.target_field_definition_id}</span>
                  {' を '}<span className="font-semibold">{EFFECT_LABELS[c.effect] ?? c.effect}</span>
                  {c.effect === 'error' && c.error_message && <p className="mt-1 text-text-body">「{c.error_message}」</p>}
                  <pre className="mt-1 text-text-sub whitespace-pre-wrap">{JSON.stringify(c.expression_ast)}</pre>
                </div>
                {canSemantic && (
                  <div className="flex shrink-0 gap-1">
                    <button type="button" aria-label="編集" onClick={() => setEditingCondition(c)} className="p-1 text-brand-red"><Pencil size={14} /></button>
                    <button type="button" aria-label="削除" onClick={() => setDeletingCondition(c)} className="p-1 text-text-sub hover:text-warning-text"><Trash2 size={14} /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="p-3 sm:p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-text-body">計算式</h3>
          {canSemantic && fields.some((f) => f.is_computed) && (
            <Button size="sm" variant="secondary" leftIcon={<Plus size={14} />} onClick={() => setEditingFormula('new')}>計算式追加</Button>
          )}
        </div>
        {formulas.length === 0 ? <p className="text-xs text-text-sub">登録されていません</p> : (
          <ul className="space-y-1">
            {formulas.map((f) => (
              <li key={f.formula_id} className="flex items-start justify-between gap-2 text-xs border border-surface-border rounded p-2">
                <div>
                  <span className="font-mono">{fieldsById.get(f.target_field_definition_id)?.field_key ?? f.target_field_definition_id}</span>
                  <pre className="mt-1 text-text-sub whitespace-pre-wrap">{JSON.stringify(f.expression_ast)}</pre>
                </div>
                {canSemantic && (
                  <div className="flex shrink-0 gap-1">
                    <button type="button" aria-label="編集" onClick={() => setEditingFormula(f)} className="p-1 text-brand-red"><Pencil size={14} /></button>
                    <button type="button" aria-label="削除" onClick={() => setDeletingFormula(f)} className="p-1 text-text-sub hover:text-warning-text"><Trash2 size={14} /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="p-3 sm:p-4 space-y-2">
        <h3 className="text-sm font-semibold text-text-body">お知らせ</h3>
        <p className="text-xs text-text-sub">
          この設定はこの申請書にだけ効きます。シフト・休暇などの承認通知は承認ワークフロー設定 (
          <a href="/approvals" className="text-brand-red underline">/approvals</a>) で制御します。
        </p>
        <NotificationSettingsMatrix basePath={`/request-types/${rtId}/notifications`} canManage={canRouting} />
      </div>

      {canRouting && (
        <div className="p-3 sm:p-4">
          <AudiencePreview requestTypeId={rtId} canRouting={canRouting} />
        </div>
      )}

      {editingField && (
        <FieldFormDialog
          open onClose={() => setEditingField(null)} submitting={saveField.isPending}
          canSemantic={canSemantic} category={requestType.category} semanticRoles={semanticRoles}
          visibilityClasses={visibilityClasses} canVisibility={canVisibility}
          initial={editingField === 'new'
            ? { field_key: '', field_type: 'text', label: null, field_options: null, semantic_role: null, is_computed: false, is_required_default: false, visibility_class_id: null, section_label: null, validation_json: null }
            : { field_key: editingField.field_key, field_type: editingField.field_type, label: editingField.label, field_options: editingField.field_options, semantic_role: editingField.semantic_role, is_computed: editingField.is_computed, is_required_default: editingField.is_required_default, visibility_class_id: editingField.visibility_class_id ?? null, section_label: editingField.section_label ?? null, validation_json: editingField.validation_json ?? null }}
          onSubmit={(v) => {
            const body: Record<string, unknown> = {
              field_key: v.field_key, field_type: v.field_type, is_computed: v.is_computed, is_required_default: v.is_required_default,
              field_options: (v.field_type === 'select' || v.field_type === 'multi_select') ? parseOptionsText(v.field_options_text) : null,
              label: v.label, validation_json: v.validation_json,
            };
            if (canSemantic) body.semantic_role = v.semantic_role;
            // ⚠️ 権限が無いときは送らない (送ると requireVisibilityIfPresent が 403 を返す)
            if (canVisibility) body.visibility_class_id = v.visibility_class_id;
            body.section_label = v.section_label;
            saveField.mutate({ id: editingField === 'new' ? undefined : editingField.field_definition_id, body });
          }}
        />
      )}
      <ConfirmDialog open={deletingField !== null} title="このフィールドを無効化しますか？" destructive confirmLabel="無効化する"
        loading={deleteField.isPending} onClose={() => setDeletingField(null)}
        onConfirm={() => { if (deletingField) deleteField.mutate(deletingField.field_definition_id); }}
        message={<p className="text-sm">条件式・計算式から参照されているフィールドは無効化できません (サーバー側でチェックされます)。</p>} />

      {editingCondition && (
        <ConditionFormDialog
          open onClose={() => setEditingCondition(null)} submitting={saveCondition.isPending} fields={fields}
          initial={editingCondition === 'new'
            ? { target_field_definition_id: fields[0]?.field_definition_id ?? '', effect: 'show', expression_ast_text: '{"op":"literal","value":true}', error_message: null }
            : {
              target_field_definition_id: editingCondition.target_field_definition_id, effect: editingCondition.effect,
              expression_ast_text: JSON.stringify(editingCondition.expression_ast, null, 2),
              error_message: editingCondition.error_message ?? null,
            }}
          onSubmit={(v) => {
            const ast = parseAstText(v.expression_ast_text);
            saveCondition.mutate({
              id: editingCondition === 'new' ? undefined : editingCondition.condition_id,
              body: { target_field_definition_id: v.target_field_definition_id, effect: v.effect, expression_ast: ast, error_message: v.error_message },
            });
          }}
        />
      )}
      <ConfirmDialog open={deletingCondition !== null} title="この条件を削除しますか？" destructive confirmLabel="削除する"
        loading={deleteCondition.isPending} onClose={() => setDeletingCondition(null)}
        onConfirm={() => { if (deletingCondition) deleteCondition.mutate(deletingCondition.condition_id); }} message={<p className="text-sm">元に戻せません。</p>} />

      {editingFormula && (
        <FormulaFormDialog
          open onClose={() => setEditingFormula(null)} submitting={saveFormula.isPending} fields={fields}
          initial={editingFormula === 'new'
            ? { target_field_definition_id: fields.find((f) => f.is_computed)?.field_definition_id ?? '', expression_ast_text: '{"op":"literal","value":0}' }
            : { target_field_definition_id: editingFormula.target_field_definition_id, expression_ast_text: JSON.stringify(editingFormula.expression_ast, null, 2) }}
          onSubmit={(v) => {
            const ast = parseAstText(v.expression_ast_text);
            saveFormula.mutate({ id: editingFormula === 'new' ? undefined : editingFormula.formula_id, body: { target_field_definition_id: v.target_field_definition_id, expression_ast: ast } });
          }}
        />
      )}
      <ConfirmDialog open={deletingFormula !== null} title="この計算式を削除しますか？" destructive confirmLabel="削除する"
        loading={deleteFormula.isPending} onClose={() => setDeletingFormula(null)}
        onConfirm={() => { if (deletingFormula) deleteFormula.mutate(deletingFormula.formula_id); }} message={<p className="text-sm">元に戻せません。</p>} />
    </div>
  );
}

export function RequestTypesSettingsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const permissions = user?.effective_permissions;
  const permsReady = usePermsReady();
  const canFields = hasPermission(permissions, 'request_type_fields', 'manage');
  const canSemantic = hasPermission(permissions, 'request_type_semantic', 'manage');
  const canRouting = hasPermission(permissions, 'request_type_routing', 'manage');
  // TD-28 D-24: 機密クラスの割当は別権限。クラス一覧は view があれば読める
  const canVisibility = hasPermission(permissions, 'request_field_visibility', 'manage');
  const canVisibilityView = hasPermission(permissions, 'request_field_visibility', 'view');

  const { data: requestTypes = [], isLoading } = useQuery<RequestType[]>({
    queryKey: ['request-types'], queryFn: () => apiFetch('/request-types'),
  });
  const { data: visibilityClasses = [] } = useQuery<Array<{ class_id: string; label: string }>>({
    queryKey: ['rfv-classes'],
    queryFn: () => apiFetch<Array<{ class_id: string; label: string }>>('/request-field-visibility/classes'),
    enabled: canVisibilityView,
  });
  const { data: semanticRoles = [] } = useQuery<SemanticRole[]>({
    queryKey: ['request-type-semantic-roles'], queryFn: () => apiFetch('/request-types/semantic-roles'),
  });

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [editingBase, setEditingBase] = useState<RequestType | null>(null);
  const [disabling, setDisabling] = useState<RequestType | null>(null);

  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const saveBase = useMutation({
    mutationFn: (v: { id?: string; body: unknown }) => apiFetch(v.id ? `/request-types/${v.id}` : '/request-types', {
      method: v.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v.body),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['request-types'] }); setCreating(false); setEditingBase(null); toast.success('保存しました'); },
    onError: (e) => toast.error(errMsg(e, '保存に失敗しました')),
  });
  const disableType = useMutation({
    mutationFn: (id: string) => apiFetch(`/request-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['request-types'] }); setDisabling(null); toast.success('無効化しました'); },
    onError: (e) => { toast.error(errMsg(e, '無効化に失敗しました')); setDisabling(null); },
  });

  // design-and-code レビュー LOW-1: GET系は全て request_type:view を要求するため (canFields/
  // canSemantic/canRouting だけでは一覧すら取得できない)、フォールバック判定は view の有無のみで行う。
  if (!permsReady) {
    return <div role="status" className="p-6 text-text-sub">読み込み中…</div>;
  }
  if (!isLoading && !hasPermission(permissions, 'request_type', 'view')) {
    return <div className="p-6 border border-surface-border rounded-lg bg-white text-text-sub">このページを表示する権限がありません。</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">申請種別</h2>
          <p className="text-sm text-text-sub mt-1">
            休暇・残業・個人情報変更等の申請フォームを構成する申請種別・入力項目・条件式・計算式を管理します。
          </p>
        </div>
        {canFields && (
          <Button leftIcon={<Plus size={16} />} onClick={() => setCreating(true)}>申請種別追加</Button>
        )}
      </div>

      {isLoading ? <div className="text-text-sub">読み込み中…</div> : requestTypes.length === 0 ? (
        <EmptyState message="申請種別が登録されていません" description="「申請種別追加」から追加してください" />
      ) : (
        <div className="space-y-2">
          {requestTypes.map((rt) => {
            const isOpen = expanded.has(rt.request_type_id);
            return (
              <div key={rt.request_type_id} className="border border-surface-border rounded-lg bg-white">
                <div className="flex items-center gap-2 px-3 sm:px-4 py-3">
                  <button type="button" onClick={() => toggle(rt.request_type_id)} aria-expanded={isOpen}
                    className="flex-1 min-w-0 flex items-center gap-2 text-left min-h-[44px]">
                    <ChevronDown size={16} aria-hidden className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-text-body truncate">{rt.label}</span>
                      <span className="block text-xs text-text-sub">
                        {CATEGORY_LABELS[rt.category] ?? rt.category} ・ {rt.code} ・ {TIMING_MODE_LABELS[rt.timing_mode] ?? rt.timing_mode}
                        {rt.status === 'disabled' && ' ・ 無効'}
                      </span>
                    </span>
                  </button>
                  {canFields && (
                    <div className="flex shrink-0 gap-1">
                      <button type="button" aria-label="編集" onClick={() => setEditingBase(rt)} className="p-2 text-brand-red hover:text-brand-red-hover"><Pencil size={16} /></button>
                      {!rt.is_platform_mandatory && rt.status === 'active' && (
                        <button type="button" aria-label="無効化" onClick={() => setDisabling(rt)} className="p-2 text-text-sub hover:text-warning-text"><Trash2 size={16} /></button>
                      )}
                    </div>
                  )}
                </div>
                {isOpen && (
                  <RequestTypeDetailPanel requestType={rt} semanticRoles={semanticRoles} canFields={canFields} canSemantic={canSemantic}
                    visibilityClasses={visibilityClasses} canVisibility={canVisibility} canRouting={canRouting} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <RequestTypeFormDialog
          open onClose={() => setCreating(false)} submitting={saveBase.isPending} canRouting={canRouting}
          initial={{
            category: 'other', code: '', label: '', timing_mode: 'standard', handler_key: 'record_only',
            eligibility_condition_set_id: null, approval_usage_point_id: null, approval_workflow_id: null, domain_ref: null,
            rejection_behavior: 'reject_only', return_step_back: 0,
            proxy_request_mode: 'inherit', proxy_requester_condition_set_id: null,
          }}
          onSubmit={(v) => {
            const body: Record<string, unknown> = { category: v.category, code: v.code, label: v.label, timing_mode: v.timing_mode, handler_key: v.handler_key };
            if (canRouting) {
              body.eligibility_condition_set_id = v.eligibility_condition_set_id;
              body.approval_usage_point_id = v.approval_usage_point_id;
              body.approval_workflow_id = v.approval_workflow_id;
              body.rejection_behavior = v.rejection_behavior;
              body.return_step_back = v.return_step_back;
              body.proxy_request_mode = v.proxy_request_mode;
              body.proxy_requester_condition_set_id = v.proxy_requester_condition_set_id;
              // ⚠️ 休暇カテゴリ以外では送らない (バックエンドが「休暇カテゴリでのみ利用できる」と拒否するため)
              if (isLeaveCategory(v.category)) body.domain_ref = v.domain_ref;
            }
            saveBase.mutate({ body });
          }}
        />
      )}
      {editingBase && (
        <RequestTypeFormDialog
          open onClose={() => setEditingBase(null)} submitting={saveBase.isPending} canRouting={canRouting}
          initial={{
            category: editingBase.category, code: editingBase.code, label: editingBase.label, timing_mode: editingBase.timing_mode,
            handler_key: editingBase.handler_key, eligibility_condition_set_id: editingBase.eligibility_condition_set_id, approval_usage_point_id: editingBase.approval_usage_point_id,
            approval_workflow_id: editingBase.approval_workflow_id,
            domain_ref: editingBase.domain_ref,
            rejection_behavior: editingBase.rejection_behavior,
            return_step_back: editingBase.return_step_back,
            proxy_request_mode: editingBase.proxy_request_mode,
            proxy_requester_condition_set_id: editingBase.proxy_requester_condition_set_id,
          }}
          onSubmit={(v) => {
            const body: Record<string, unknown> = { category: v.category, code: v.code, label: v.label, timing_mode: v.timing_mode, handler_key: v.handler_key };
            if (canRouting) {
              body.eligibility_condition_set_id = v.eligibility_condition_set_id;
              body.approval_usage_point_id = v.approval_usage_point_id;
              body.approval_workflow_id = v.approval_workflow_id;
              body.rejection_behavior = v.rejection_behavior;
              body.return_step_back = v.return_step_back;
              body.proxy_request_mode = v.proxy_request_mode;
              body.proxy_requester_condition_set_id = v.proxy_requester_condition_set_id;
              // ⚠️ 休暇カテゴリ以外では送らない (バックエンドが「休暇カテゴリでのみ利用できる」と拒否するため)
              if (isLeaveCategory(v.category)) body.domain_ref = v.domain_ref;
            }
            saveBase.mutate({ id: editingBase.request_type_id, body });
          }}
        />
      )}
      <ConfirmDialog open={disabling !== null} title="この申請種別を無効化しますか？" destructive confirmLabel="無効化する"
        loading={disableType.isPending} onClose={() => setDisabling(null)}
        onConfirm={() => { if (disabling) disableType.mutate(disabling.request_type_id); }}
        message={<p className="text-sm">既存の申請には影響しませんが、以後この種別で新規申請できなくなります。</p>} />
    </div>
  );
}
